import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import CitizenMedicalHistory from '../citizen/CitizenMedicalHistory';
import CitizenExam from '../citizen/CitizenExam';
import CitizenInvestigations from '../citizen/CitizenInvestigations';
import CitizenPrescriptions from '../citizen/CitizenPrescriptions';
import VisitWorkspaceHeader from '../../components/visit/VisitWorkspaceHeader';
import VisitWorkspaceTabs, { WORKSPACE_SECTIONS } from '../../components/visit/VisitWorkspaceTabs';
import VisitCompletionPanel from '../../components/visit/VisitCompletionPanel';
import ClinicalGuidancePanel from '../../components/visit/ClinicalGuidancePanel';
import useVisitWorkspace from '../../hooks/useVisitWorkspace';

const VALID_SECTIONS = new Set(WORKSPACE_SECTIONS.map((item) => item.key));

const SECTION_LABELS = Object.fromEntries(
  WORKSPACE_SECTIONS.map((item) => [item.key, item.label || item.title || item.key])
);

function safeText(value) {
  return value == null ? '' : String(value).trim();
}

function normalizeCorrectionSection(value) {
  const section = safeText(value).toLowerCase().replace(/[\s_-]+/g, '-');
  const aliases = {
    exam: 'examination',
    examinations: 'examination',
    investigation: 'investigations',
    prescriptions: 'prescription',
    rx: 'prescription',
    visit: 'general',
    overall: 'general',
  };
  return aliases[section] || section;
}

function normalizeCorrectionItem(item = {}) {
  if (!item || typeof item !== 'object' || Array.isArray(item)) {
    return { section: '', field: '', instruction: '' };
  }
  return {
    section: normalizeCorrectionSection(item.section || item.sectionKey || item.area),
    field: safeText(item.field || item.fieldKey || item.label || item.issue),
    instruction: safeText(item.instruction || item.comment || item.message || item.note),
  };
}

function dedupeCorrectionItems(items = []) {
  const seen = new Set();
  return items
    .map(normalizeCorrectionItem)
    .filter((item) => item.section || item.field || item.instruction)
    .filter((item) => {
      const key = [item.section, item.field, item.instruction]
        .map((value) => safeText(value).toLowerCase())
        .join('|');
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function correctionItemsFromVisit(visit = {}) {
  const candidates = [
    visit?.correctionItems,
    visit?.returnItems,
    visit?.flaggedCorrections,
    visit?.reviewCorrections,
    visit?.prescription?.correctionItems,
    visit?.prescription?.returnItems,
    visit?.prescription?.flaggedCorrections,
    visit?.prescription?.reviewCorrections,
  ];

  // Prefer an explicit structured alias when the Visit endpoint exposes one.
  // Ignore empty arrays so a later compatible alias can still supply the data.
  const explicitSource = candidates.find((value) => Array.isArray(value) && value.length > 0);
  if (explicitSource) return dedupeCorrectionItems(explicitSource);

  // The canonical Visit document stores reviewer return items as reviewComments.
  // Queue/prescription endpoints may expose correctionItems aliases, while the
  // full Visit Workspace fetch can legitimately expose only reviewComments.
  const reviewComments = Array.isArray(visit?.reviewComments) ? visit.reviewComments : [];
  if (reviewComments.length) {
    const returnedAtValue = visit?.prescription?.returnedAt || visit?.returnedAt || '';
    const returnedAt = returnedAtValue ? new Date(returnedAtValue).getTime() : Number.NaN;
    const currentCycle = reviewComments.filter((item) => {
      if (!Number.isFinite(returnedAt)) return true;
      const createdAtValue = item?.createdAt || item?.at || item?.updatedAt || '';
      if (!createdAtValue) return true;
      const createdAt = new Date(createdAtValue).getTime();
      if (!Number.isFinite(createdAt)) return true;
      // Comments are appended as part of the same return transaction. A small
      // tolerance protects against timestamp ordering/serialization differences
      // without reviving comments from an earlier return cycle.
      return createdAt >= returnedAt - (2 * 60 * 1000);
    });
    const normalizedComments = dedupeCorrectionItems(currentCycle);
    if (normalizedComments.length) return normalizedComments;
  }

  // Legacy compatibility: older returned Visits may carry only the combined
  // return note. Show it rather than silently hiding the reviewer's instruction.
  const returnNote = safeText(visit?.returnNote || visit?.prescription?.returnNote);
  return returnNote
    ? [{ section: 'general', field: '', instruction: returnNote }]
    : [];
}

function readStoredReviewFlags(visitId) {
  if (!visitId) return [];
  try {
    const rawValue = localStorage.getItem(`visitReviewFlags:${visitId}`);
    const parsed = rawValue ? JSON.parse(rawValue) : [];
    return Array.isArray(parsed)
      ? parsed.map(normalizeCorrectionItem).filter((item) => item.section || item.field || item.instruction)
      : [];
  } catch {
    return [];
  }
}

function writeStoredReviewFlags(visitId, items = []) {
  if (!visitId) return;
  try {
    localStorage.setItem(
      `visitReviewFlags:${visitId}`,
      JSON.stringify(
        items.map(normalizeCorrectionItem).filter((item) => item.section || item.field || item.instruction)
      )
    );
  } catch {}
}

function sectionStatus(progress) {
  if (progress?.status === 'returned') return 'returned';
  return progress?.status === 'saved' || progress?.savedAt ? 'saved' : 'not_started';
}



function tenantBaseFromPath(pathname = '') {
  const match = String(pathname || '').match(/^\/t\/[^/]+/);
  return match ? match[0] : '';
}

function clearCompletedVisitStorage(citizenId, visitId) {
  try {
    const scopedKey = citizenId ? `activeVisitId:${citizenId}` : '';
    if (scopedKey && (!visitId || localStorage.getItem(scopedKey) === visitId)) {
      localStorage.removeItem(scopedKey);
    }
    ['activeVisitId', 'visitId', 'editReturnedVisit', 'activeVisitContext'].forEach((key) => {
      if (!visitId || key === 'editReturnedVisit' || localStorage.getItem(key) === visitId) {
        localStorage.removeItem(key);
      }
    });
  } catch {}
}

function captureActiveVisitStorage(citizenId) {
  try {
    const keys = [
      'activeVisitId',
      'visitId',
      'editReturnedVisit',
      'activeVisitContext',
      citizenId ? `activeVisitId:${citizenId}` : '',
    ].filter(Boolean);
    return Object.fromEntries(keys.map((key) => [key, localStorage.getItem(key)]));
  } catch {
    return {};
  }
}

function restoreActiveVisitStorage(snapshot = {}) {
  try {
    Object.entries(snapshot).forEach(([key, value]) => {
      if (value === null || value === undefined) localStorage.removeItem(key);
      else localStorage.setItem(key, value);
    });
  } catch {}
}

export default function VisitWorkspace() {
  const { citizenId = '', visitId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const query = useMemo(() => new URLSearchParams(location.search || ''), [location.search]);
  const requestedSection = query.get('section') || 'history';
  const section = VALID_SECTIONS.has(requestedSection) ? requestedSection : 'history';
  const explicitReadOnly = query.get('mode') === 'readonly' || location.state?.readOnly === true;
  const reviewerMode = query.get('review') === '1' || location.state?.reviewerMode === true;
  const activeVisitSnapshotRef = useRef(null);
  if (explicitReadOnly && activeVisitSnapshotRef.current === null) {
    activeVisitSnapshotRef.current = captureActiveVisitStorage(citizenId);
  }

  const workspace = useVisitWorkspace({ citizenId, visitId, persistActive: !explicitReadOnly });
  const {
    visit,
    sectionProgress,
    loading,
    error,
    identityMismatch,
    refresh,
  } = workspace;

  const [historyDirty, setHistoryDirty] = useState(false);
  const [historyCanSave, setHistoryCanSave] = useState(false);
  const [historySaving, setHistorySaving] = useState(false);

  const [examinationDirty, setExaminationDirty] = useState(false);
  const [examinationCanSave, setExaminationCanSave] = useState(false);
  const [examinationSaving, setExaminationSaving] = useState(false);
  const examinationSaveRef = useRef(null);

  const [investigationsDirty, setInvestigationsDirty] = useState(false);
  const [investigationsCanSave, setInvestigationsCanSave] = useState(false);
  const [investigationsSaving, setInvestigationsSaving] = useState(false);
  const investigationsSaveRef = useRef(null);

  const [prescriptionDirty, setPrescriptionDirty] = useState(false);
  const [prescriptionCanSave, setPrescriptionCanSave] = useState(false);
  const [prescriptionSaving, setPrescriptionSaving] = useState(false);
  const prescriptionSaveRef = useRef(null);

  const [reviewFlags, setReviewFlags] = useState(() => readStoredReviewFlags(visitId));
  const [reviewField, setReviewField] = useState('');
  const [reviewInstruction, setReviewInstruction] = useState('');
  const [reviewFlagMessage, setReviewFlagMessage] = useState('');

  const visitStatus = String(visit?.prescription?.status || visit?.status || '').toLowerCase();
  const statusReadOnly = ['submitted', 'pending', 'approved', 'completed', 'finalized', 'finalised', 'closed'].includes(visitStatus);
  const readOnly = explicitReadOnly || statusReadOnly;

  const returnedCorrectionItems = useMemo(
    () => correctionItemsFromVisit(visit),
    [visit]
  );

  const currentSectionCorrections = useMemo(
    () => (
      visitStatus === 'returned'
        ? returnedCorrectionItems.filter(
            (item) => !item.section || item.section === 'general' || item.section === section
          )
        : []
    ),
    [returnedCorrectionItems, section, visitStatus]
  );

  const currentReviewFlags = useMemo(
    () => reviewFlags.filter((item) => item.section === section),
    [reviewFlags, section]
  );

  useEffect(() => {
    if (!reviewerMode) return;
    setReviewFlags(readStoredReviewFlags(visitId));
  }, [reviewerMode, visitId]);

  useEffect(() => {
    if (!reviewerMode) return;
    writeStoredReviewFlags(visitId, reviewFlags);
  }, [reviewerMode, reviewFlags, visitId]);

  useEffect(() => {
    setReviewField('');
    setReviewInstruction('');
    setReviewFlagMessage('');
  }, [section]);

  useEffect(() => {
    if (!explicitReadOnly || !activeVisitSnapshotRef.current) return undefined;

    // Some legacy embedded section components still write activeVisitId while
    // resolving their compatibility context. Restore the real current Visit
    // after mount and again on exit so historical viewing cannot hijack it.
    restoreActiveVisitStorage(activeVisitSnapshotRef.current);
    return () => restoreActiveVisitStorage(activeVisitSnapshotRef.current || {});
  }, [explicitReadOnly, section, visitId]);

  useEffect(() => {
    const onCan = (event) => {
      const detail = event?.detail || {};
      if (detail.visitId && detail.visitId !== visitId) return;
      if (detail.section && detail.section !== 'history') return;
      if (section !== 'history') return;
      const canSave = detail.canSave ?? detail.can ?? detail.canContinue;
      setHistoryCanSave(Boolean(canSave));
    };

    const onBusy = (event) => {
      const detail = event?.detail || {};
      if (detail.visitId && detail.visitId !== visitId) return;
      if (detail.section && detail.section !== 'history') return;
      if (section === 'history') setHistorySaving(Boolean(detail.busy));
    };

    window.addEventListener('encounter:can-continue', onCan);
    window.addEventListener('encounter:busy', onBusy);
    return () => {
      window.removeEventListener('encounter:can-continue', onCan);
      window.removeEventListener('encounter:busy', onBusy);
    };
  }, [visitId, section]);

  const setSection = useCallback((nextSection) => {
    if (!VALID_SECTIONS.has(nextSection)) return;
    const next = new URLSearchParams(location.search || '');
    next.set('visitId', visitId);
    next.set('section', nextSection);
    navigate(`${location.pathname}?${next.toString()}`, {
      replace: true,
      state: location.state,
    });
  }, [location.pathname, location.search, location.state, navigate, visitId]);

  const handleHistorySaved = useCallback(async () => {
    setHistoryDirty(false);
    await refresh();
  }, [refresh]);

  const handleExaminationSaved = useCallback(async () => {
    setExaminationDirty(false);
    await refresh();
  }, [refresh]);

  const handleInvestigationsSaved = useCallback(async () => {
    setInvestigationsDirty(false);
    await refresh();
  }, [refresh]);

  const handlePrescriptionSaved = useCallback(async () => {
    setPrescriptionDirty(false);
    await refresh();
  }, [refresh]);

  const saveHistory = useCallback(() => {
    if (readOnly || !visitId || historySaving) return;
    window.dispatchEvent(
      new CustomEvent('encounter:save', {
        detail: { visitId, intent: 'save-section', section: 'history' },
      })
    );
  }, [readOnly, visitId, historySaving]);

  const registerExaminationSave = useCallback((handler) => {
    examinationSaveRef.current = typeof handler === 'function' ? handler : null;
  }, []);

  const saveExamination = useCallback(async () => {
    if (readOnly || !visitId || examinationSaving || !examinationSaveRef.current) return;
    await examinationSaveRef.current();
  }, [readOnly, visitId, examinationSaving]);

  const registerInvestigationsSave = useCallback((handler) => {
    investigationsSaveRef.current = typeof handler === 'function' ? handler : null;
  }, []);

  const saveInvestigations = useCallback(async () => {
    if (readOnly || !visitId || investigationsSaving || !investigationsSaveRef.current) return;
    await investigationsSaveRef.current();
  }, [readOnly, visitId, investigationsSaving]);

  const registerPrescriptionSave = useCallback((handler) => {
    prescriptionSaveRef.current = typeof handler === 'function' ? handler : null;
  }, []);

  const savePrescription = useCallback(async () => {
    if (readOnly || !visitId || prescriptionSaving || !prescriptionSaveRef.current) return;
    await prescriptionSaveRef.current();
  }, [readOnly, visitId, prescriptionSaving]);

  const addCurrentSectionFlag = useCallback(() => {
    if (!reviewerMode || !visitId) return;
    const instruction = safeText(reviewInstruction);
    if (!instruction) {
      setReviewFlagMessage('Enter a correction instruction before flagging this section.');
      return;
    }

    const nextItem = {
      section,
      field: safeText(reviewField),
      instruction,
    };
    setReviewFlags((current) => [...current, nextItem]);
    setReviewField('');
    setReviewInstruction('');
    setReviewFlagMessage('Correction flag added. It will be available in the Return Visit for Correction dialog.');
  }, [reviewerMode, reviewField, reviewInstruction, section, visitId]);

  const removeReviewFlag = useCallback((indexToRemove) => {
    setReviewFlags((current) => current.filter((_item, index) => index !== indexToRemove));
    setReviewFlagMessage('Correction flag removed.');
  }, []);

  const goBackToReviewerWorkbench = useCallback(() => {
    const fallback = `${tenantBaseFromPath(location.pathname)}/dashboard/approval?tab=mine`;
    const from = safeText(location.state?.from);
    navigate(from || fallback);
  }, [location.pathname, location.state, navigate]);

  const handleVisitCompleted = useCallback(async (payload = {}) => {
    const resultingStatus = String(payload?.currentStatus || payload?.status || '').toLowerCase();
    const wasReturned = visitStatus === 'returned' || (() => {
      try { return localStorage.getItem('editReturnedVisit') === visitId; } catch { return false; }
    })();

    try { await refresh(); } catch {}
    clearCompletedVisitStorage(citizenId, visitId);

    const tenantBase = tenantBaseFromPath(location.pathname);
    if (wasReturned && resultingStatus === 'submitted') {
      navigate(`${tenantBase}/approvals?tab=returned`, { replace: true });
      return;
    }

    navigate(`${tenantBase}/dashboard/operations`, {
      replace: true,
      state: {
        redirectedFrom: resultingStatus === 'submitted' ? 'workspace-submit' : 'workspace-complete',
        focusSection: 'citizen-services',
        visitCompleted: true,
        citizenId,
        visitId,
      },
    });
  }, [citizenId, location.pathname, navigate, refresh, visitId, visitStatus]);

  const statuses = useMemo(() => {
    const progress = sectionProgress || {};
    return {
      history: historySaving
        ? 'saving'
        : historyDirty
          ? 'dirty'
          : sectionStatus(progress.history),
      examination: examinationSaving
        ? 'saving'
        : examinationDirty
          ? 'dirty'
          : sectionStatus(progress.examination),
      investigations: investigationsSaving
        ? 'saving'
        : investigationsDirty
          ? 'dirty'
          : sectionStatus(progress.investigations),
      prescription: prescriptionSaving
        ? 'saving'
        : prescriptionDirty
          ? 'dirty'
          : sectionStatus(progress.prescription),
    };
  }, [
    sectionProgress,
    historyDirty,
    historySaving,
    examinationDirty,
    examinationSaving,
    investigationsDirty,
    investigationsSaving,
    prescriptionDirty,
    prescriptionSaving,
  ]);

  if (loading && !visit) {
    return (
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CircularProgress size={24} />
        <Typography variant="body2">Loading Visit workspace…</Typography>
      </Box>
    );
  }

  if (error || identityMismatch || !visit) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Unable to open this Visit workspace.'}
        </Alert>
        <Button variant="outlined" onClick={() => refresh()}>
          Retry
        </Button>
      </Box>
    );
  }


  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <VisitWorkspaceHeader visit={visit} />

      {reviewerMode ? (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={goBackToReviewerWorkbench}>
              Back to Workbench
            </Button>
          }
        >
          Reviewer mode — read-only. Review the complete submitted Visit, flag the exact section/field needing correction,
          then return to Reviewer Workbench to Approve or Return the Visit.
        </Alert>
      ) : readOnly ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Historical Visit — read-only. Viewing this Visit does not replace the citizen&apos;s active/current Visit.
        </Alert>
      ) : null}

      {visitStatus === 'returned' && returnedCorrectionItems.length ? (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="returned-reviewer-corrections">
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
            Reviewer corrections for this returned Visit
          </Typography>
          <Stack spacing={0.75}>
            {returnedCorrectionItems.map((item, index) => (
              <Box
                key={`${item.section}-${item.field}-${index}`}
                data-testid={`returned-correction-${index}`}
              >
                <strong>{SECTION_LABELS[item.section] || item.section || 'Visit'}</strong>
                {item.field ? ` → ${item.field}` : ''}
                {item.instruction ? `: ${item.instruction}` : ''}
              </Box>
            ))}
          </Stack>
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <VisitWorkspaceTabs section={section} statuses={statuses} onChange={setSection} />

        {currentSectionCorrections.length ? (
          <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pt: 2 }}>
            <Alert severity="warning">
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                Corrections requested in {SECTION_LABELS[section] || section}
              </Typography>
              <Stack spacing={0.75}>
                {currentSectionCorrections.map((item, index) => (
                  <Box key={`${item.field}-${index}`}>
                    {item.field ? <strong>{item.field}: </strong> : null}
                    {item.instruction || 'Correction requested.'}
                  </Box>
                ))}
              </Stack>
            </Alert>
          </Box>
        ) : null}

        {reviewerMode ? (
          <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pt: 2 }}>
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
              <Stack spacing={1.25}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Flag for correction — {SECTION_LABELS[section] || section}
                  </Typography>
                  <Chip size="small" variant="outlined" label={`${currentReviewFlags.length} flag${currentReviewFlags.length === 1 ? '' : 's'} in this section`} />
                </Box>

                {currentReviewFlags.length ? (
                  <Stack spacing={0.75}>
                    {reviewFlags.map((item, index) => (
                      item.section === section ? (
                        <Box
                          key={`${item.section}-${item.field}-${index}`}
                          sx={{
                            display: 'flex',
                            gap: 1,
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1.5,
                            p: 1,
                          }}
                        >
                          <Typography variant="body2">
                            {item.field ? <strong>{item.field}: </strong> : null}
                            {item.instruction}
                          </Typography>
                          <Button size="small" color="error" onClick={() => removeReviewFlag(index)}>
                            Remove
                          </Button>
                        </Box>
                      ) : null
                    ))}
                  </Stack>
                ) : null}

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                  <TextField
                    size="small"
                    label="Exact field / issue (recommended)"
                    value={reviewField}
                    onChange={(event) => setReviewField(event.target.value)}
                    placeholder="e.g., HOPI → onset, BP, ordered investigation, diagnosis"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Correction instruction"
                    value={reviewInstruction}
                    onChange={(event) => setReviewInstruction(event.target.value)}
                    placeholder="State exactly what should be corrected, added or rechecked."
                    sx={{ flex: 2 }}
                  />
                  <Button variant="outlined" onClick={addCurrentSectionFlag}>
                    Flag for correction
                  </Button>
                </Stack>

                {reviewFlagMessage ? (
                  <Typography variant="caption" color={reviewFlagMessage.startsWith('Enter') ? 'error' : 'text.secondary'}>
                    {reviewFlagMessage}
                  </Typography>
                ) : null}
              </Stack>
            </Paper>
          </Box>
        ) : null}

        <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          <Box sx={{ display: { xs: 'block', md: 'grid' }, gridTemplateColumns: { md: 'minmax(0, 1fr) 320px' }, gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
          {/* Keep migrated sections mounted so unsaved drafts survive tab switches. */}
          <Box sx={{ display: section === 'history' ? 'block' : 'none' }}>
            <CitizenMedicalHistory
              embedded
              onSectionDirty={() => setHistoryDirty(true)}
              onSectionSaved={handleHistorySaved}
            />
            {reviewerMode ? (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button size="small" variant="text" onClick={() => setReviewField('History section')}>
                  Flag this section for correction
                </Button>
              </Box>
            ) : null}
            {!readOnly ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                onClick={saveHistory}
                disabled={!historyCanSave || historySaving}
              >
                {historySaving ? 'Saving History…' : 'Save History'}
              </Button>
            </Box>
            ) : null}
          </Box>

          <Box sx={{ display: section === 'examination' ? 'block' : 'none' }}>
            <CitizenExam
              embedded
              visitId={visitId}
              citizenId={citizenId}
              visit={visit}
              onSectionDirty={() => setExaminationDirty(true)}
              onSectionSaved={handleExaminationSaved}
              onCanSave={setExaminationCanSave}
              onSavingChange={setExaminationSaving}
              registerSave={registerExaminationSave}
            />
            {reviewerMode ? (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button size="small" variant="text" onClick={() => setReviewField('Examination section')}>
                  Flag this section for correction
                </Button>
              </Box>
            ) : null}
            {!readOnly ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                onClick={saveExamination}
                disabled={!examinationCanSave || examinationSaving}
              >
                {examinationSaving ? 'Saving Examination…' : 'Save Examination'}
              </Button>
            </Box>
            ) : null}
          </Box>

          <Box sx={{ display: section === 'investigations' ? 'block' : 'none' }}>
            <CitizenInvestigations
              embedded
              visitId={visitId}
              citizenId={citizenId}
              visit={visit}
              onSectionDirty={() => setInvestigationsDirty(true)}
              onSectionSaved={handleInvestigationsSaved}
              onCanSave={setInvestigationsCanSave}
              onSavingChange={setInvestigationsSaving}
              registerSave={registerInvestigationsSave}
            />
            {reviewerMode ? (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button size="small" variant="text" onClick={() => setReviewField('Investigations section')}>
                  Flag this section for correction
                </Button>
              </Box>
            ) : null}
            {!readOnly ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                onClick={saveInvestigations}
                disabled={!investigationsCanSave || investigationsSaving}
              >
                {investigationsSaving ? 'Saving Investigations…' : 'Save Investigations'}
              </Button>
            </Box>
            ) : null}
          </Box>

          <Box sx={{ display: section === 'prescription' ? 'block' : 'none' }}>
            <CitizenPrescriptions
              embedded
              visitId={visitId}
              citizenId={citizenId}
              visit={visit}
              onSectionDirty={() => setPrescriptionDirty(true)}
              onSectionSaved={handlePrescriptionSaved}
              onCanSave={setPrescriptionCanSave}
              onSavingChange={setPrescriptionSaving}
              registerSave={registerPrescriptionSave}
            />
            {reviewerMode ? (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button size="small" variant="text" onClick={() => setReviewField('Prescription section')}>
                  Flag this section for correction
                </Button>
              </Box>
            ) : null}
            {!readOnly ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                onClick={savePrescription}
                disabled={!prescriptionCanSave || prescriptionSaving}
              >
                {prescriptionSaving ? 'Saving Prescription…' : 'Save Prescription'}
              </Button>
            </Box>
            ) : null}
          </Box>
            </Box>
            <Box sx={{ mt: { xs: 2, md: 0 } }}>
              <ClinicalGuidancePanel visit={visit} section={section} />
            </Box>
          </Box>
        </Box>
      </Paper>

      {!readOnly ? (
        <VisitCompletionPanel
          statuses={statuses}
          visitId={visitId}
          visit={visit}
          onCompleted={handleVisitCompleted}
        />
      ) : null}
    </Box>
  );
}
